import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "speech-schedule-container" }
const _hoisted_2 = { class: "speech-input-section" }
const _hoisted_3 = { class: "input-group" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = {
  key: 0,
  class: "error-message"
}
const _hoisted_9 = {
  key: 0,
  class: "loading"
}
const _hoisted_10 = {
  key: 1,
  class: "schedule-results"
}
const _hoisted_11 = { class: "schedule-grid" }

import { ref, computed, watch } from 'vue'
import { useSpeechRecognition } from '@vueuse/core'
import { format, differenceInDays, isValid, isAfter } from 'date-fns'


export default /*@__PURE__*/_defineComponent({
  __name: 'SpeechSchedule',
  props: {
    baseUrl: {}
  },
  emits: ["schedule-loaded"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// ============ STATE ============
const spokenQuery = ref('')
const parsedStartDate = ref<Date | null>(null)
const parsedEndDate = ref<Date | null>(null)
const errorMessage = ref('')
const isLoading = ref(false)
const scheduleData = ref<any>(null)
const dateList = ref<any[]>([])

// Speech recognition
const {
  isSupported,
  isListening,
  result,
  interimResults,
  start,
  stop,
} = useSpeechRecognition({
  lang: 'en-US',
  continuous: false,
  interimResults: true,
})

// Watch for final speech result
watch(result, (newText) => {
  if (newText) {
    spokenQuery.value = newText
    processSpeechQuery()
  }
})

// Watch for interim results
const interimText = ref('')
watch(interimResults, (newText) => {
  interimText.value = newText || ''
})

// ============ DATE PARSING ============
const parseDateFromText = (text: string): Date | null => {
  // Common date patterns
  const patterns = [
    /(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+(\d{4}))?/i,
    /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})(?:st|nd|rd|th)?/i,
    /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/,
    /(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/,
    /(today|tomorrow)/i,
  ]

  const currentYear = new Date().getFullYear()

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match) {
      if (match[0].toLowerCase() === 'today') {
        return getDateWithoutTime(new Date())
      }
      if (match[0].toLowerCase() === 'tomorrow') {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        return getDateWithoutTime(tomorrow)
      }

      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December']
      
      let day, month, year

      if (match[1] && monthNames.some(m => m.toLowerCase() === match[2]?.toLowerCase())) {
        day = parseInt(match[1])
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === match[2].toLowerCase())
        month = monthIndex + 1
        year = match[3] ? parseInt(match[3]) : currentYear
      }
      else if (match[1] && monthNames.some(m => m.toLowerCase() === match[1].toLowerCase())) {
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === match[1].toLowerCase())
        month = monthIndex + 1
        day = parseInt(match[2])
        year = currentYear
      }
      else if (match[1] && match[2] && match[3]) {
        const first = parseInt(match[1])
        const second = parseInt(match[2])
        const yearVal = parseInt(match[3])
        
        if (first > 12 && second <= 12) {
          day = first
          month = second
        } else if (first <= 12 && second > 12) {
          day = second
          month = first
        } else {
          day = first
          month = second
        }
        year = yearVal
      }
      else {
        continue
      }

      // Create date WITHOUT timezone offset
      const date = createDateWithoutTimezone(year, month - 1, day)
      if (isValid(date)) {
        return date
      }
    }
  }

  return null
}

// Helper function to create date without timezone offset
const createDateWithoutTimezone = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day))
}

// Helper function to get date without time
const getDateWithoutTime = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

const extractDatesFromQuery = (query: string): { start: Date | null; end: Date | null } => {
  // Common date markers
  const patterns = [
    /between\s+([^.]+?)\s+and\s+([^.]+?)(?:\s|$)/i,
    /from\s+([^.]+?)\s+to\s+([^.]+?)(?:\s|$)/i,
    /(\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?)\s+(?:to|and|through)\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?)/i,
  ]

  for (const pattern of patterns) {
    const match = query.match(pattern)
    if (match) {
      const startDate = parseDateFromText(match[1])
      const endDate = parseDateFromText(match[2])
      
      if (startDate && endDate) {
        return { start: startDate, end: endDate }
      }
    }
  }

  // Try to find two dates anywhere in the text
  const allDates = query.match(/\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?/gi)
  if (allDates && allDates.length >= 2) {
    const start = parseDateFromText(allDates[0])
    const end = parseDateFromText(allDates[allDates.length - 1])
    if (start && end) {
      return { start, end }
    }
  }

  // Try single date (use today as start)
  const singleDate = parseDateFromText(query)
  if (singleDate) {
    return { start: singleDate, end: singleDate }
  }

  return { start: null, end: null }
}

// ============ QUERY PROCESSING ============
const processSpeechQuery = async () => {
  if (!spokenQuery.value.trim()) {
    errorMessage.value = 'Please speak or type a query.'
    return
  }

  errorMessage.value = ''
  isLoading.value = true

  try {
    const { start, end } = extractDatesFromQuery(spokenQuery.value)
    
    if (!start || !end) {
      errorMessage.value = 'Could not find dates in your query.'
      isLoading.value = false
      return
    }

    if (isAfter(start, end)) {
      errorMessage.value = 'Start date cannot be after end date.'
      isLoading.value = false
      return
    }

    parsedStartDate.value = start
    parsedEndDate.value = end

    // Call the schedule API
    await fetchSchedule(start, end)

  } catch (error) {
    console.error('Error processing query:', error)
    errorMessage.value = 'Failed to process your request. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// ============ API CALL ============
const fetchSchedule = async (startDate: Date, endDate: Date) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      errorMessage.value = 'Please login first.'
      return
    }

    const response = await fetch(props.baseUrl + 'schedule/ScheduleDictionaryForDateRangePageWise', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        StartDate: startDate,
        EndDate: endDate,
        PageNumber: 0,
        RecordsPerPage: 5000,
        SearchString: '',
        CompanyId: 0
      })
    })

    if (response.status === 401) {
      errorMessage.value = 'Session expired. Please login again.'
      return
    }

    const data = await response.json()
    const scheduleDataResult = data.scheduleSinglePageResult || {}

    // Process dates for display
    const dateKeys = Object.keys(scheduleDataResult).sort()
    const dates = dateKeys.map(key => ({
      dateKey: key,
      date: new Date(key)
    }))

    scheduleData.value = scheduleDataResult
    dateList.value = dates

    // ✅ FIX: Emit BOTH start and end dates to parent
    emit('schedule-loaded', { 
      schedule: scheduleDataResult, 
      dates,
      startDate: startDate,
      endDate: endDate
    })

  } catch (error) {
    console.error('Error fetching schedule:', error)
    throw error
  }
}

// ============ TOGGLE RECORDING ============
const toggleRecording = () => {
  if (isListening.value) {
    stop()
  } else {
    spokenQuery.value = ''
    parsedStartDate.value = null
    parsedEndDate.value = null
    errorMessage.value = ''
    start()
  }
}

// ============ HELPERS ============
const formatDate = (date: Date): string => {
  return format(date, 'dd MMM yyyy')
}

const daysBetween = (start: Date, end: Date): number => {
  return differenceInDays(end, start) + 1
}

const grandTotalHours = computed(() => {
  if (!scheduleData.value) return '0h'
  // Calculate total hours from schedule data
  // Your existing calculation here
  return '0h'
})

// ============ EXAMPLE QUERIES ============
const examples = [
  'show schedule between 1st January and 15th January',
  'show schedule from today to next week',
  'show schedule between 5 March and 20 March',
  'show schedule for 15 February'
]

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: toggleRecording,
          class: _normalizeClass(["mic-button", { recording: _unref(isListening) }]),
          disabled: !_unref(isSupported)
        }, [
          (!_unref(isSupported))
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, "❌"))
            : (_unref(isListening))
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, "⏹ Stop"))
              : (_openBlock(), _createElementBlock("span", _hoisted_7, "🎤 Speak"))
        ], 10, _hoisted_4),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((spokenQuery).value = $event)),
          type: "text",
          placeholder: "e.g., \"Between 1 January and 7 January\"",
          class: "query-input",
          onKeyup: _withKeys(processSpeechQuery, ["enter"])
        }, null, 544), [
          [_vModelText, spokenQuery.value]
        ])
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, " ⚠️ " + _toDisplayString(errorMessage.value), 1))
        : _createCommentVNode("", true)
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("div", { class: "spinner" }, null, -1),
          _createTextVNode(" Loading schedule... ", -1)
        ]))]))
      : _createCommentVNode("", true),
    (scheduleData.value && !isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _renderSlot(_ctx.$slots, "schedule-display", {
              schedule: scheduleData.value,
              dates: dateList.value
            })
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})